import Grid from '@mui/material/Grid2';
import { Avatar, Chip, Typography, useTheme } from '@mui/material';
import { useGetAgentTemplates } from 'apis/business/agent/hook';
import { EmptyStateView } from 'components/Common/EmptyStateView';
import Spinner from 'components/Common/Spinner';
import { useEffect } from 'react';

interface AgentTemplateGridProps {
  selectedIndustry: number | string;
  selectedAgent: number | null;
  setSelectedAgent: (id: number | null) => void;
}

export default function AgentTemplateGrid({
  selectedIndustry,
  selectedAgent,
  setSelectedAgent
}: AgentTemplateGridProps) {
  const theme = useTheme();
  const { data: agents, isLoading } = useGetAgentTemplates({
    filter: { industry_eq: selectedIndustry }
  });

  // Maintain selection if still available
  useEffect(() => {
    if (!agents?.data?.length) {
      setSelectedAgent(null);
      return;
    }

    const exists = agents.data.some((agent) => agent.id === selectedAgent);
    if (!exists) {
      setSelectedAgent(agents.data[0].id);
    }
  }, [agents, selectedAgent, setSelectedAgent]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!agents?.data.length) {
    return <EmptyStateView />;
  }

  return (
    <Grid container spacing={2}>
      {agents.data
        .sort((a, b) => {
          if (a.name === 'Blank Agent') return -1;
          if (b.name === 'Blank Agent') return 1;
          return 0;
        })
        .map((agent) => (
          <Grid
            size={{ xs: 6, sm: 4, md: 3 }}
            key={agent.id}
            sx={{
              padding: 2,
              border: '2px solid',
              borderRadius: 2,
              cursor: 'pointer',
              borderColor: selectedAgent === agent.id ? theme.palette.primary.main : '#EEE'
            }}
            onClick={() => setSelectedAgent(agent.id)}
          >
            <Avatar
              src={agent.avatar_url ?? undefined}
              alt={agent.name}
              sx={{
                width: 30,
                height: 30,
                marginBottom: 1
              }}
            />
            <Typography variant='body1'>{agent.agent_name}</Typography>
            <Typography
              variant='body2'
              color='secondary'
              title={agent.description}
              sx={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                WebkitLineClamp: 2,
                lineHeight: '1.5em',
                minHeight: '3em'
              }}
            >
              {agent.description}
            </Typography>
            {agent.name && (
              <Chip
                label={agent.name.replace(/_/g, ' ')}
                color='info'
                size='small'
                sx={{ fontSize: '12px', marginTop: 1, border: 1, borderColor: '#ECECEC', background: '#fff' }}
              />
            )}
            {agent.agent_interaction && (
              <Chip
                label={agent.agent_interaction.replace(/_/g, ' ')}
                color='info'
                size='small'
                sx={{
                  marginLeft: '5px',
                  fontSize: '12px',
                  marginTop: 1,
                  border: 1,
                  borderColor: '#ECECEC',
                  background: '#fff'
                }}
              />
            )}
          </Grid>
        ))}
    </Grid>
  );
}
